import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
} from 'recharts'
import { SOURCE_CHANNELS } from '../../constants/ticketFormOptions'

/* ── stat card config ── */
const STAT_CONFIG = [
  { key: 'total_tickets',      label: 'Total Tickets',      color: '#6366F1' },
  { key: 'assigned_tickets',   label: 'Assigned Tickets',   color: '#7C3AED' },
  { key: 'unassigned_tickets', label: 'Unassigned Tickets', color: '#D97706' },
  { key: 'closed_tickets',     label: 'Closed Tickets',     color: '#16A34A' },
  { key: 'sla_breached',       label: 'SLA Breached',       color: '#EF4444' },
]

const TREND_COLOR = '#2563EB'

const FUNNEL_INFO = {
  L1: { color: '#3B82F6', desc: 'Front-line support — active tickets being handled at the first escalation level.' },
  L2: { color: '#8B5CF6', desc: 'Second-level support — tickets escalated from L1 for deeper investigation.' },
  L3: { color: '#F59E0B', desc: 'Third-level support — complex issues escalated for specialized handling.' },
  L4: { color: '#EF4444', desc: 'Critical escalation — highest priority tickets requiring immediate attention.' },
}

const QUICK_PERIODS = [
  { label: 'Today',         value: 'today' },
  { label: 'Last 7 Days',   value: 'last_7_days' },
  { label: 'Last 30 Days',  value: 'last_30_days' },
  { label: 'Last 6 Months', value: 'last_6_months' },
]

/* ── DateFilterDropdown ── */
function DateFilterDropdown({ value, onChange }) {
  const [open, setOpen]       = useState(false)
  const [fromDate, setFrom]   = useState('')
  const [toDate,   setTo]     = useState('')
  const ref = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const displayLabel = () => {
    if (value.type === 'custom' && value.from && value.to) {
      const fmt = d => d.split('-').reverse().join('/')
      return `${fmt(value.from)} – ${fmt(value.to)}`
    }
    return QUICK_PERIODS.find(p => p.value === value.period)?.label || 'Today'
  }

  const selectQuick = (period) => {
    onChange({ type: 'quick', period })
    setOpen(false)
  }

  const applyCustom = () => {
    if (!fromDate || !toDate) return
    onChange({ type: 'custom', from: fromDate, to: toDate })
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', border: '1px solid #D1D5DB',
          borderRadius: 8, background: '#fff', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: '#374151',
          whiteSpace: 'nowrap',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="4" width="14" height="13" rx="2" />
          <path d="M3 8h14M7 2v4M13 2v4" strokeLinecap="round" />
        </svg>
        {displayLabel()}
        <span style={{ fontSize: 9, color: '#6B7280' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 230, padding: '8px 0',
        }}>
          {QUICK_PERIODS.map(p => (
            <div
              key={p.value}
              onClick={() => selectQuick(p.value)}
              style={{
                padding: '10px 18px', cursor: 'pointer', fontSize: 13,
                fontWeight: value.type === 'quick' && value.period === p.value ? 700 : 400,
                color: value.type === 'quick' && value.period === p.value ? '#2563EB' : '#111827',
                background: value.type === 'quick' && value.period === p.value ? '#EFF6FF' : '#fff',
              }}
              onMouseEnter={e => { if (!(value.type === 'quick' && value.period === p.value)) e.currentTarget.style.background = '#F9FAFB' }}
              onMouseLeave={e => { e.currentTarget.style.background = value.type === 'quick' && value.period === p.value ? '#EFF6FF' : '#fff' }}
            >
              {p.label}
            </div>
          ))}

          {/* Custom range */}
          <div style={{ borderTop: '1px solid #F3F4F6', margin: '6px 0 0', padding: '10px 18px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', marginBottom: 10 }}>
              CUSTOM RANGE
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>From</div>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFrom(e.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB',
                  borderRadius: 6, fontSize: 12, color: '#374151', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>To</div>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={e => setTo(e.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB',
                  borderRadius: 6, fontSize: 12, color: '#374151', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              onClick={applyCustom}
              disabled={!fromDate || !toDate}
              style={{
                width: '100%', padding: '9px 0',
                background: (!fromDate || !toDate) ? '#93C5FD' : '#2563EB',
                color: '#fff', border: 'none', borderRadius: 7,
                fontSize: 13, fontWeight: 700, cursor: (!fromDate || !toDate) ? 'not-allowed' : 'pointer',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── CategorySearchSelect — searchable dropdown that hits the API with ?q= ── */
function CategorySearchSelect({ value, onChange, placeholder, fetchJson }) {
  const [open, setOpen]           = useState(false)
  const [query, setQuery]         = useState('')
  const [options, setOptions]     = useState([])
  const [loading, setLoading]     = useState(false)
  const [cachedLabel, setCached]  = useState('')
  const ref       = useRef(null)
  const hasLoaded = useRef(false)

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  /* load all categories once on first open */
  useEffect(() => {
    if (!open || hasLoaded.current || !fetchJson) return
    hasLoaded.current = true
    setLoading(true)
    fetchJson('/api/categories/category/get-categories?type=sub_category')
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.results || data?.data || [])
        setOptions(list.map(c => ({ value: c.slug, label: c.name })))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [open, fetchJson])

  /* re-fetch with ?q= only when the user types (debounced) */
  useEffect(() => {
    if (!hasLoaded.current || !fetchJson) return
    const t = setTimeout(() => {
      setLoading(true)
      const url = query
        ? `/api/categories/category/get-categories?type=sub_category&q=${encodeURIComponent(query)}`
        : '/api/categories/category/get-categories?type=sub_category'
      fetchJson(url)
        .then(data => {
          const list = Array.isArray(data) ? data : (data?.results || data?.data || [])
          setOptions(list.map(c => ({ value: c.slug, label: c.name })))
        })
        .catch(() => {})
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(t)
  }, [query, fetchJson])

  const select = (o) => { onChange(o.value); setCached(o.label); setOpen(false); setQuery('') }
  const clear   = ()  => { onChange('');     setCached('');       setOpen(false); setQuery('') }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 28px 6px 12px', border: '1px solid #D1D5DB',
          borderRadius: 8, background: '#fff', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: value ? '#374151' : '#9CA3AF',
          maxWidth: 160, position: 'relative',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value ? (cachedLabel || value) : placeholder}
        </span>
        <span style={{ position: 'absolute', right: 9, fontSize: 9, color: '#6B7280', pointerEvents: 'none' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 230, padding: 8,
        }}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search…"
            style={{
              width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB',
              borderRadius: 7, fontSize: 12, color: '#374151', outline: 'none',
              boxSizing: 'border-box', marginBottom: 6,
            }}
          />
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {value && (
              <div
                onClick={clear}
                style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', borderRadius: 6 }}
                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                {placeholder}
              </div>
            )}
            {loading ? (
              <div style={{ padding: '8px 10px', fontSize: 12, color: '#9CA3AF' }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: 12, color: '#9CA3AF' }}>No results</div>
            ) : options.map(o => (
              <div
                key={o.value}
                onClick={() => select(o)}
                style={{
                  padding: '8px 10px', cursor: 'pointer', fontSize: 12, borderRadius: 6,
                  fontWeight: o.value === value ? 700 : 400,
                  color: o.value === value ? '#2563EB' : '#111827',
                  background: o.value === value ? '#EFF6FF' : '#fff',
                }}
                onMouseEnter={e => { if (o.value !== value) e.currentTarget.style.background = '#F9FAFB' }}
                onMouseLeave={e => { e.currentTarget.style.background = o.value === value ? '#EFF6FF' : '#fff' }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ── SimpleSelect — for channel / region / category ── */
function SimpleSelect({ value, onChange, options, placeholder }) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          appearance: 'none', padding: '6px 28px 6px 12px',
          border: '1px solid #D1D5DB', borderRadius: 8,
          fontSize: 12, fontWeight: 600, color: value ? '#374151' : '#9CA3AF',
          background: '#fff', cursor: 'pointer', outline: 'none', maxWidth: 160,
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ position: 'absolute', right: 9, pointerEvents: 'none', fontSize: 9, color: '#6B7280' }}>▼</span>
    </div>
  )
}

/* ── shared layout helpers ── */
function StatCard({ label, value, color, loading }) {
  return (
    <div style={{
      flex: 1, minWidth: 0, overflow: 'hidden',
      background: '#fff', border: '1px solid #E5E7EB',
      borderRadius: 8, padding: '10px 14px',
    }}>
      <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 500, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>
        {loading ? <span style={{ color: '#D1D5DB' }}>—</span> : (value ?? 0).toLocaleString()}
      </div>
    </div>
  )
}

function SectionCard({ title, children, style }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #E5E7EB',
      borderRadius: 8, padding: '10px 14px', ...style,
    }}>
      {title && <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{title}</div>}
      {children}
    </div>
  )
}

function EmptyState({ text = 'No data' }) {
  return <div style={{ padding: '12px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>{text}</div>
}

function Loader({ height = 80 }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 13 }}>
      Loading…
    </div>
  )
}

function channelChartHeight(n)  { return Math.max(80, n * 28 + 16) }
function categoryChartHeight(n) { return Math.max(100, n * 30 + 16) }
function estimateLabelWidth(items, key) {
  const longest = items.reduce((max, d) => Math.max(max, (d[key] || '').length), 0)
  return Math.min(Math.max(longest * 6.5, 60), 180)
}
function CategoryTick({ x, y, payload }) {
  const label = payload?.value || ''
  const short = label.length > 14 ? label.slice(0, 14) + '…' : label
  return (
    <text x={x} y={y} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="#374151" style={{ cursor: 'default' }}>
      <title>{label}</title>
      {short}
    </text>
  )
}

/* ── main component ── */
export default function AdminPage({ fetchJson }) {

  /* filters */
  const [dateFilter, setDateFilter] = useState({ type: 'quick', period: 'today' })
  const [channel,    setChannel]    = useState('')
  const [regionId,   setRegionId]   = useState('')
  const [categorySlug, setCategorySlug] = useState('')

  /* filter option lists */
  const [regionsList, setRegionsList] = useState([])

  /* dashboard data */
  const [trendData,    setTrendData]    = useState([])
  const [ticketCount,  setTicketCount]  = useState(null)
  const [channelData,  setChannelData]  = useState([])
  const [funnelData,   setFunnelData]   = useState(null)
  const [hoveredFunnel, setHoveredFunnel] = useState(null)
  const [categoryData, setCategoryData] = useState([])
  const [regionData,   setRegionData]   = useState([])
  const [cityData,     setCityData]     = useState([])

  const [trendLoading, setTrendLoading] = useState(false)
  const [dashLoading,  setDashLoading]  = useState(false)
  const [error,        setError]        = useState('')
  const isFirstLoad = useRef(true)

  /* ── fetch regions ── */
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/regions')
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.data || data?.results || [])
        setRegionsList(list.map(r => ({ value: r.slug, label: r.name })))
      })
      .catch(() => {})
  }, [fetchJson])

  /* ── build query params ── */
  const buildParams = useCallback(() => {
    const p = new URLSearchParams()
    const fmt = d => d.toISOString().split('T')[0]  // YYYY-MM-DD

    if (dateFilter.type === 'custom') {
      if (dateFilter.from) p.set('from_date', dateFilter.from)
      if (dateFilter.to)   p.set('to_date',   dateFilter.to)
    } else {
      const to   = new Date()
      const from = new Date()
      switch (dateFilter.period) {
        case 'last_7_days':   from.setDate(from.getDate() - 6);        break
        case 'last_30_days':  from.setDate(from.getDate() - 29);       break
        case 'last_6_months': from.setMonth(from.getMonth() - 6);      break
        default: break  // 'today' → from === to === today
      }
      p.set('from_date', fmt(from))
      p.set('to_date',   fmt(to))
    }

    if (channel)      p.set('channel',      channel)
    if (regionId)     p.set('region',       regionId)
    if (categorySlug) p.set('sub_category', categorySlug)
    return p.toString()
  }, [dateFilter, channel, regionId, categorySlug])

  /* ── parse API response ── */
  const applyResponse = useCallback((data, includeTrend = false) => {
    if (!data) return
    setTicketCount(data.ticket_count || null)

    if (includeTrend && Array.isArray(data.ticket_trends)) {
      const sorted = [...data.ticket_trends].reverse()
      setTrendData(sorted.map(t => ({
        day:             t.day,
        'Total Tickets': t.total_ticket,
        'Reopened':      t.reopened,
        'In Progress':   t.in_progress,
        'Closed':        t.closed,
      })))
    }

    if (Array.isArray(data.tickets_by_channel)) {
      setChannelData(data.tickets_by_channel)
    }

    if (data.escalation_funnel) {
      const f = data.escalation_funnel
      const max = Math.max(f.l1 || 0, f.l2 || 0, f.l3 || 0, f.l4 || 0, 1)
      setFunnelData([
        { level: 'L1', count: f.l1 || 0, max },
        { level: 'L2', count: f.l2 || 0, max },
        { level: 'L3', count: f.l3 || 0, max },
        { level: 'L4', count: f.l4 || 0, max },
      ])
    }

    if (Array.isArray(data.top_issue_categories)) {
      setCategoryData(data.top_issue_categories.map(d => ({
        category: d.sub_category__name || '—',
        count: d.count,
      })))
    }

    if (Array.isArray(data.region_tickets_count)) {
      setRegionData(data.region_tickets_count.map(d => ({
        region: d.region__name || '—',
        tickets: d.count,
      })))
    }

    if (Array.isArray(data.city_tickets_count)) {
      setCityData(data.city_tickets_count.map(d => ({
        city: d.incident_city__name || '—',
        tickets: d.count,
      })))
    }
  }, [])

  /* ── initial load (trend + all) ── */
  useEffect(() => {
    if (!fetchJson) return
    setTrendLoading(true)
    setDashLoading(true)
    setError('')
    // TODO: replace with real API endpoint
    fetchJson('/api/tickets/ticket-admin/ticket-details/')
      .then(data => applyResponse(data, true))
      .catch(e => setError(e.message || 'Failed to load dashboard'))
      .finally(() => { setTrendLoading(false); setDashLoading(false) })
  }, [fetchJson, applyResponse])

  /* ── re-fetch on filter change (trend stays) ── */
  useEffect(() => {
    if (!fetchJson) return
    if (isFirstLoad.current) { isFirstLoad.current = false; return }
    setDashLoading(true)
    setError('')
    // TODO: replace with real API endpoint
    fetchJson(`/api/tickets/ticket-admin/ticket-details/?${buildParams()}`)
      .then(data => applyResponse(data, false))
      .catch(e => setError(e.message || 'Failed to load dashboard'))
      .finally(() => setDashLoading(false))
  }, [fetchJson, buildParams, applyResponse])

  const handleRefresh = () => {
    if (!fetchJson) return
    setTrendLoading(true)
    setDashLoading(true)
    fetchJson(`/api/tickets/ticket-admin/ticket-details/?${buildParams()}`)
      .then(data => applyResponse(data, true))
      .catch(e => setError(e.message || 'Failed to load dashboard'))
      .finally(() => { setTrendLoading(false); setDashLoading(false) })
  }

  const channelYWidth = estimateLabelWidth(channelData, 'channel')

  const card = (extra = {}) => ({
    background: '#fff', border: '1px solid #E5E7EB',
    borderRadius: 10, padding: '14px 16px',
    ...extra,
  })
  const cardTitle = { fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }

  return (
    <div style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14, background: '#F5F7FA' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Manager Dashboard</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <DateFilterDropdown value={dateFilter} onChange={setDateFilter} />
          <SimpleSelect value={channel}      onChange={setChannel}      placeholder="All Channels"   options={SOURCE_CHANNELS} />
          <SimpleSelect value={regionId}     onChange={setRegionId}     placeholder="All Regions"    options={regionsList} />
          <CategorySearchSelect value={categorySlug} onChange={setCategorySlug} placeholder="All Categories" fetchJson={fetchJson} />
          <button
            onClick={handleRefresh}
            style={{
              width: 30, height: 30, border: '1px solid #D1D5DB',
              borderRadius: 7, background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, color: '#374151',
            }}
            title="Refresh"
          >↻</button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA',
          color: '#DC2626', borderRadius: 8, padding: '8px 12px', fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        {STAT_CONFIG.map(c => (
          <StatCard key={c.key} label={c.label} color={c.color} value={ticketCount?.[c.key]} loading={dashLoading} />
        ))}
      </div>

      {/* ── Row 1: Trend (58%) + Channel & Funnel (42%) ── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

        {/* Trend chart */}
        <div style={card({ flex: '0 0 calc(58% - 6px)', minWidth: 0 })}>
          <div style={cardTitle}>Ticket Trend (Last 7 Days)</div>
          <div style={{ height: 260 }}>
            {trendLoading ? <Loader height={260} /> : trendData.length === 0 ? <EmptyState text="No trend data" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 4, right: 12, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Line type="monotone" dataKey="Total Tickets" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3, fill: '#2563EB', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Reopened"      stroke="#F59E0B" strokeWidth={2}   dot={{ r: 3, fill: '#F59E0B', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="In Progress"   stroke="#6366F1" strokeWidth={2}   dot={{ r: 3, fill: '#6366F1', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Closed"        stroke="#16A34A" strokeWidth={2}   dot={{ r: 3, fill: '#16A34A', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Right column: By Channel + Escalation Funnel */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* By Channel */}
          <div style={card({})}>
            <div style={cardTitle}>By Channel</div>
            <div style={{ overflowY: 'auto' }}>
              {dashLoading ? <Loader height={80} /> : channelData.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={channelChartHeight(channelData.length)}>
                  <BarChart data={channelData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barSize={10}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="channel" tick={{ fontSize: 10, fill: '#374151' }} axisLine={false} tickLine={false} width={channelYWidth} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} cursor={{ fill: '#F3F4F6' }} />
                    <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Escalation Funnel */}
          <div style={card({})}>
            <div style={{ ...cardTitle, position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              onMouseEnter={() => setHoveredFunnel('__title__')}
              onMouseLeave={() => setHoveredFunnel(null)}
            >
              Escalation Funnel
              <svg width="13" height="13" viewBox="0 0 20 20" fill={hoveredFunnel === '__title__' ? '#6366F1' : '#9CA3AF'} style={{ flexShrink: 0, transition: 'fill .15s', cursor: 'default' }}>
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 100 2 1 1 0 000-2zm-1 4a1 1 0 112 0v3a1 1 0 11-2 0v-3z" clipRule="evenodd" />
              </svg>
              {hoveredFunnel === '__title__' && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30,
                  background: '#1E293B', color: '#F1F5F9', borderRadius: 8,
                  padding: '9px 12px', fontSize: 12, lineHeight: 1.5, whiteSpace: 'nowrap',
                  boxShadow: '0 4px 14px rgba(0,0,0,.2)', pointerEvents: 'none', fontWeight: 400,
                }}>
                  Number of <strong style={{ color: '#A5B4FC' }}>active tickets</strong> currently at each escalation level
                </div>
              )}
            </div>
            {dashLoading ? <Loader height={80} /> : !funnelData ? <EmptyState /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {funnelData.map(({ level, count, max }) => {
                  const info = FUNNEL_INFO[level] || { color: '#2563EB' }
                  return (
                    <div key={level}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{level}</span>
                        <span style={{ fontSize: 12, color: info.color, fontWeight: 700 }}>{count.toLocaleString()}</span>
                      </div>
                      <div style={{ height: 7, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, width: `${(count / max) * 100}%`, background: info.color, transition: 'width .4s ease' }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 2: Top Issue Categories (55%) + Region (22.5%) + City (22.5%) ── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

        {/* Top Issue Categories */}
        <div style={card({ flex: '0 0 calc(55% - 8px)', minWidth: 0 })}>
          <div style={cardTitle}>Top Issue Categories</div>
          <div style={{ overflowY: 'auto' }}>
            {dashLoading ? <Loader height={80} /> : categoryData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={categoryChartHeight(categoryData.length)}>
                <BarChart data={categoryData} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barSize={12}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="category" tick={(props) => <CategoryTick {...props} />} axisLine={false} tickLine={false} width={100} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} cursor={{ fill: '#F3F4F6' }} />
                  <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Region Wise Tickets */}
        <div style={card({ flex: 1, minWidth: 0 })}>
          <div style={cardTitle}>Region Wise Tickets</div>
          {dashLoading ? <Loader height={80} /> : regionData.length === 0 ? <EmptyState /> : (() => {
            const maxTickets = Math.max(...regionData.map(r => r.tickets), 1)
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {regionData.map(({ region, tickets }) => (
                  <div key={region}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{region}</span>
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 700 }}>{tickets.toLocaleString()}</span>
                    </div>
                    <div style={{ height: 7, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, width: `${(tickets / maxTickets) * 100}%`, background: '#2563EB', transition: 'width .4s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>

        {/* City Wise Tickets */}
        <div style={card({ flex: 1, minWidth: 0 })}>
          <div style={cardTitle}>City Wise Tickets</div>
          {dashLoading ? <Loader height={80} /> : cityData.length === 0 ? <EmptyState /> : (() => {
            const maxTickets = Math.max(...cityData.map(c => c.tickets), 1)
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cityData.map(({ city, tickets }) => (
                  <div key={city}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{city}</span>
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 700 }}>{tickets.toLocaleString()}</span>
                    </div>
                    <div style={{ height: 7, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, width: `${(tickets / maxTickets) * 100}%`, background: '#2563EB', transition: 'width .4s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>

      </div>

    </div>
  )
}
